import React, { useEffect, useRef } from "react";
import {
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";
import Ionicons from "@expo/vector-icons/Ionicons";
import { LinearGradient } from "expo-linear-gradient";
import { displayFont, uiFont } from "../../styles/agapellaFonts";
import type { useHomeRoulette } from "./useHomeRoulette";
import Svg, { Path } from "react-native-svg";
import { HomeAssistantSparkles } from "./HomeAssistantSparkles";
import {
  HOME_ROULETTE_MOTION,
  nextHomeOrbitAngle,
  homeOrbitProgress,
  homePetalAtOrbitAngle,
} from "./homeRoulette";
import {
  HOME_DIRECTION_ICON_PATHS,
  HOME_DIRECTION_ICON_VIEWBOXES,
} from "./homeDirectionArtwork";

export interface HomeDirection {
  title: string;
  directions: string;
  icon: React.ComponentProps<typeof Ionicons>["name"];
}
export interface PetalPlacement {
  contentLeft: number;
  contentTop: number;
  contentWidth: number;
  contentHeight: number;
}
interface Props {
  roulette: ReturnType<typeof useHomeRoulette>;
  items: readonly HomeDirection[];
  isDark: boolean;
  isMobile: boolean;
  language: "ru" | "en" | "es";
  placements: readonly PetalPlacement[];
  renderFlower: (
    highlighted: number | null,
    selected: number | null,
  ) => React.ReactNode;
  onDirection: (index: number) => void;
  onAssistant: () => void;
}
const labels = {
  ru: {
    random: "Не знаю, с чего начать",
    caption: "Случайный выбор",
    spinning: "Выбираем направление…",
    result: "Выбрано направление",
    close: "Закрыть рулетку",
    selected: "Выбрано",
    open: "Перейти в направление",
  },
  en: {
    random: "Not sure where to begin",
    caption: "Random choice",
    spinning: "Choosing a direction…",
    result: "Selected direction",
    close: "Close random choice",
    selected: "Selected",
    open: "Open direction",
  },
  es: {
    random: "No sé por dónde empezar",
    caption: "Elección aleatoria",
    spinning: "Eligiendo una dirección…",
    result: "Dirección elegida",
    close: "Cerrar selección",
    selected: "Elegida",
    open: "Abrir dirección",
  },
};

export function HomeDirections({
  roulette,
  items,
  isDark,
  isMobile,
  language,
  placements,
  renderFlower,
  onDirection,
  onAssistant,
}: Props) {
  const { state, start, cancel, reducedMotion, destination } = roulette;
  const [hovered, setHovered] = React.useState<number | null>(null);
  const copy = labels[language];
  const selecting = state.phase === "spinning";
  const [orbitAngle, setOrbitAngle] = React.useState(0);
  const orbitAngleRef = useRef(0);
  const [orbitHighlight, setOrbitHighlight] = React.useState<number | null>(
    null,
  );
  useEffect(() => {
    if (destination === null || isMobile) return;
    if (reducedMotion) {
      const target = nextHomeOrbitAngle(
        orbitAngleRef.current,
        destination,
        true,
      );
      orbitAngleRef.current = target;
      setOrbitAngle(target);
      setOrbitHighlight(null);
      return;
    }
    if (!selecting) {
      if (state.phase === "result") {
        const target = nextHomeOrbitAngle(
          orbitAngleRef.current,
          destination,
          true,
        );
        orbitAngleRef.current = target;
        setOrbitAngle(target);
      }
      return;
    }
    const from = orbitAngleRef.current;
    const to = nextHomeOrbitAngle(from, destination, false);
    const started = performance.now();
    setOrbitAngle(to);
    let frame = 0;
    let lastHighlight: number | null = null;
    const tick = () => {
      const progress = homeOrbitProgress(performance.now() - started);
      orbitAngleRef.current = from + (to - from) * progress;
      const nextHighlight = homePetalAtOrbitAngle(orbitAngleRef.current);
      if (nextHighlight !== lastHighlight) {
        lastHighlight = nextHighlight;
        setOrbitHighlight(nextHighlight);
      }
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      // Freeze at the same position if focus/visibility cancels the selection.
      setOrbitAngle(orbitAngleRef.current);
      setOrbitHighlight(null);
    };
  }, [selecting, destination, reducedMotion, isMobile, state.phase]);
  const selected = state.phase === "result" ? state.selected : null;
  const highlighted = selecting
    ? isMobile
      ? state.highlighted
      : orbitHighlight
    : state.phase === "idle"
      ? hovered
      : null;
  const openDirection = (index: number) => {
    if (selecting) return;
    cancel();
    onDirection(index);
  };
  const textColor = isDark ? "#F9F3E5" : "#314C48";
  const grid = () => (
    <View
      style={styles.grid}
      testID="home-directions-grid"
    >
      {/* Same semantic order as the desktop flower; mobile reference reads by rows. */}
      {[0, 1, 5, 2, 4, 3]
        .filter((index) => index < items.length)
        .map((index) => {
          const item = items[index];
          const chosen = selected === index;
          const passing = highlighted === index;
          return (
            <Pressable
              key={item.title}
              {...({
                dataSet: {
                  rouletteSelected: chosen ? "true" : "false",
                  homeTheme: isDark ? "dark" : "light",
                },
              } as object)}
              accessibilityRole="button"
              accessibilityLabel={`${chosen ? `${copy.selected}: ` : ""}${item.title}`}
              accessibilityState={{ selected: chosen, disabled: selecting }}
              disabled={selecting}
              onPress={() => openDirection(index)}
              style={({ pressed }) => [
                styles.card,
                {
                  borderColor: chosen || passing
                    ? isDark ? "#EBC98D" : "#C59B61"
                    : isDark ? "#8B9384" : "#D9CEAA",
                  borderWidth: chosen || passing ? 2 : 1,
                  opacity: pressed ? 0.88 : 1,
                },
              ]}
              testID={`direction-${index}`}
            >
              <LinearGradient
                colors={
                  isDark ? ["#7E9188", "#354B60"] : ["#FFF9DC", "#A7C5BC"]
                }
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={StyleSheet.absoluteFill}
              />
              <View
                pointerEvents="none"
                style={[
                  StyleSheet.absoluteFill,
                  {
                    backgroundColor: isDark ? "#FFF0BD" : "#FFFBDD",
                    opacity: chosen ? isDark ? 0.32 : 0.78 : passing ? isDark ? 0.22 : 0.48 : 0,
                  },
                  Platform.OS === "web" &&
                    ({
                      transitionProperty: "opacity",
                      transitionDuration: reducedMotion ? "0ms" : "140ms",
                    } as object),
                ]}
              />
              <Svg
                testID="home-direction-icon"
                style={{ position: "relative", zIndex: 1 }}
                width={30}
                height={32}
                viewBox={HOME_DIRECTION_ICON_VIEWBOXES[index]}
              >
                <Path
                  d={HOME_DIRECTION_ICON_PATHS[index]}
                  fill={isDark ? chosen || passing ? "#FFF0C8" : "#F7C88F" : "#AC8153"}
                />
              </Svg>
              <Text
                testID="home-direction-title"
                style={[
                  styles.cardTitle,
                  { color: isDark ? chosen || passing ? "#FFF8E7" : "#E0E7DC" : "#314C48" },
                ]}
              >
                {item.title}
              </Text>
            </Pressable>
          );
        })}
    </View>
  );
  const status =
    state.phase === "result"
      ? `${copy.result}: ${items[state.selected].title}`
      : "";
  const randomButton = (
    <Pressable
      testID="home-roulette-start"
      accessibilityRole="button"
      accessibilityState={{ disabled: selecting, busy: selecting }}
      disabled={selecting}
      onPress={start}
      style={[
        styles.random,
        !isMobile && styles.centerAction,
        !isMobile &&
          Platform.OS === "web" &&
          ({
            backgroundImage: `radial-gradient(ellipse, ${isDark ? "rgba(255,207,139,0.23)" : "rgba(255,255,235,0.85)"} 0%, transparent 72%)`,
            textShadow: isDark
              ? "0 0 12px rgba(255,216,164,0.45)"
              : "0 0 10px rgba(255,249,209,0.8)",
          } as object),
      ]}
    >
      {isMobile && (
        <LinearGradient
          colors={["#FF9756", "#FFD3A6"]}
          start={{ x: 0, y: 0.5 }}
          end={{ x: 1, y: 0.5 }}
          style={StyleSheet.absoluteFill}
        />
      )}
      {isMobile && (
        <Ionicons
          name="shuffle-outline"
          size={isMobile ? 28 : 23}
          color={isMobile ? "#3D352B" : textColor}
        />
      )}
      <View style={styles.randomCopy}>
        <Text
          testID={isMobile ? "home-mobile-roulette-prompt" : "home-roulette-prompt"}
          style={[
            styles.randomTitle,
            !isMobile && {
              color: textColor,
              textAlign: "center",
              ...displayFont(),
              fontSize: 14,
              lineHeight: 18,
            },
          ]}
        >
          {isMobile ? copy.random : `${copy.random}.`}
        </Text>
        {isMobile && <Text style={styles.randomCaption}>{copy.caption}</Text>}
      </View>
    </Pressable>
  );
  return (
    <View
      testID="home-directions"
      {...((Platform.OS === "web"
        ? {
            dataSet: {
              roulettePhase: state.phase,
              homeTheme: isDark ? "dark" : "light",
              motion: reducedMotion ? "reduced" : "full",
            },
          }
        : {}) as object)}
      style={styles.container}
    >
      {isMobile ? (
        <>
          {grid()}
          {randomButton}
        </>
      ) : (
        <View testID="home-desktop-flower" style={styles.flower}>
          <View pointerEvents="none" style={StyleSheet.absoluteFill}>
            {renderFlower(highlighted, selected)}
          </View>
          {items.map((item, index) => {
            const p = placements[index];
            return (
              <Pressable
                key={item.title}
                testID={`home-direction-${index}`}
                {...({
                  dataSet: {
                    rouletteSelected: selected === index ? "true" : "false",
                    homeTheme: isDark ? "dark" : "light",
                  },
                } as object)}
                accessibilityRole="button"
                accessibilityLabel={`${selected === index ? `${copy.selected}: ` : ""}${item.title}. ${item.directions}`}
                accessibilityState={{
                  selected: selected === index,
                  disabled: selecting,
                }}
                disabled={selecting}
                onPress={() => openDirection(index)}
                onHoverIn={() => setHovered(index)}
                onHoverOut={() => setHovered(null)}
                onFocus={() => setHovered(index)}
                onBlur={() => setHovered(null)}
                style={[
                  styles.petal,
                  {
                    left: p.contentLeft,
                    top: p.contentTop,
                    width: p.contentWidth,
                    minHeight: p.contentHeight,
                  },
                ]}
              >
                <Svg
                  testID="home-direction-icon"
                  style={{ position: "relative", zIndex: 1 }}
                  width={21}
                  height={22}
                  viewBox={HOME_DIRECTION_ICON_VIEWBOXES[index]}
                >
                  <Path
                    d={HOME_DIRECTION_ICON_PATHS[index]}
                    fill={isDark ? "#F7C88F" : "#AC8153"}
                  />
                </Svg>
                <Text
                  testID="home-direction-title"
                  style={[
                    styles.petalTitle,
                    { color: isDark ? "#E0E7DC" : "#314C48" },
                  ]}
                >
                  {item.title}
                </Text>
                <Text
                  style={[
                    styles.petalCaption,
                    { color: isDark ? "#C4D0C4" : "#49685B" },
                    { maxWidth: [120, 138, 110, 100, 96, 110][index] },
                  ]}
                >
                  {item.directions}
                </Text>
              </Pressable>
            );
          })}
          <View
            pointerEvents="none"
            style={[
              styles.centerGlow,
              {
                borderColor: isDark
                  ? "rgba(255,216,156,0.38)"
                  : "rgba(255,241,192,0.9)",
                ...(Platform.OS === "web"
                  ? {
                      boxShadow: isDark
                        ? "0 0 20px 3px rgba(255,201,131,0.18), inset 0 0 16px rgba(255,223,164,0.12)"
                        : "0 0 18px 3px rgba(255,210,132,0.25), inset 0 0 16px rgba(255,253,219,0.5)",
                    }
                  : {}),
              },
            ]}
          />
          <View style={styles.center}>
            {randomButton}
            <Pressable
              accessibilityRole="button"
              testID="home-flower-assistant"
              onPress={() => {
                cancel();
                onAssistant();
              }}
              style={styles.centerAssistant}
            >
              <Text testID="home-flower-assistant-label" style={[styles.centerAssistantText, { color: textColor }]}>
                {language === "ru"
                  ? "Спросить Agapella"
                  : language === "es"
                    ? "Preguntar a Agapella"
                    : "Ask Agapella"}
              </Text>
              <HomeAssistantSparkles isDark={isDark} />
            </Pressable>
          </View>
          <View pointerEvents="box-none" style={styles.orbit}>
            <View
              pointerEvents="box-none"
              testID="home-roulette-orbit"
              style={[
                StyleSheet.absoluteFill,
                { transform: [{ rotate: `${orbitAngle}deg` }] },
                Platform.OS === "web" &&
                  ({
                    transition: `transform ${selecting && !reducedMotion ? HOME_ROULETTE_MOTION.durationMs : 0}ms cubic-bezier(0.34, 0.08, 0.16, 1)`,
                  } as object),
              ]}
            >
              <Pressable
                testID="home-roulette-orb"
                accessibilityRole="button"
                accessibilityLabel={copy.caption}
                accessibilityState={{ disabled: selecting, busy: selecting }}
                disabled={selecting}
                onPress={start}
                style={styles.orbHit}
              >
                <View pointerEvents="none" style={styles.orbHalo}>
                  <LinearGradient
                    colors={["#FFF9E8", "#FFD797", "#EC9855", "#9E4828"]}
                    locations={[0, 0.28, 0.66, 1]}
                    start={{ x: 0.18, y: 0.12 }}
                    end={{ x: 0.86, y: 0.9 }}
                    style={styles.orbSphere}
                  >
                    <View style={styles.orbHighlight} />
                  </LinearGradient>
                </View>
              </Pressable>
            </View>
          </View>
        </View>
      )}
      <Text accessibilityLiveRegion="polite" style={styles.screenReaderOnly}>
        {status}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { width: "100%", alignItems: "center", gap: 12 },
  grid: { width: "100%", flexDirection: "row", flexWrap: "wrap", gap: 10 },
  card: {
    flexBasis: "47%",
    flexGrow: 1,
    minHeight: 138,
    borderRadius: 26,
    overflow: "hidden",
    padding: 16,
    gap: 14,
    alignItems: "center",
    justifyContent: "center",
  },
  cardTitle: {
    ...uiFont(600),
    fontSize: 14,
    lineHeight: 19,
    textAlign: "center",
  },

  random: {
    width: "100%",
    minHeight: 58,
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 18,
    overflow: "hidden",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
  },
  randomCopy: { flexShrink: 1, gap: 2 },
  randomTitle: {
    ...uiFont(700),
    fontSize: 14,
    lineHeight: 20,
    color: "#3D352B",
  },
  randomCaption: {
    ...uiFont(),
    fontSize: 12,
    lineHeight: 17,
    color: "#4F4337",
  },
  flower: { width: 534, height: 416 },
  petal: {
    position: "absolute",
    padding: 0,
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 7,
    borderRadius: 24,
  },
  petalTitle: {
    ...uiFont(700),
    maxWidth: 116,
    fontSize: 14,
    lineHeight: 17,
    textAlign: "center",
  },
  petalCaption: {
    ...uiFont(),
    fontSize: 10,
    lineHeight: 12,
    textAlign: "center",
  },
  centerGlow: {
    position: "absolute",
    left: 182.045,
    top: 118.94,
    width: 167.482,
    height: 167.482,
    borderRadius: 84,
    borderWidth: 1,
  },
  center: { position: "absolute", left: 182.045, top: 118.94, width: 167.482 },
  centerAction: {
    position: "absolute",
    left: 16,
    top: 39,
    width: 135.482,
    minHeight: 44,
    paddingHorizontal: 0,
    paddingVertical: 0,
    borderRadius: 10,
  },
  centerAssistant: {
    position: "absolute",
    left: 12,
    top: 91,
    width: 143.482,
    minHeight: 62,
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 9,
    borderRadius: 12,
  },
  centerAssistantText: {
    ...uiFont(700),
    fontSize: 14,
    lineHeight: 18,
    textAlign: "center",
  },
  screenReaderOnly: {
    position: "absolute",
    width: 1,
    height: 1,
    overflow: "hidden",
    opacity: 0,
  },
  orbit: {
    position: "absolute",
    left: 175.786,
    top: 112.68,
    width: 180,
    height: 180,
  },
  orbHit: {
    position: "absolute",
    left: 68,
    top: -22,
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: "center",
    justifyContent: "center",
  },
  orbHalo: {
    width: 26,
    height: 26,
    borderRadius: 13,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,222,158,0.28)",
    shadowColor: "#F0AD68",
    shadowOpacity: 0.85,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 0 },
  },
  orbSphere: {
    width: 14,
    height: 14,
    borderRadius: 7,
    borderWidth: 0.6,
    borderColor: "#FFDE93",
    overflow: "hidden",
  },
  orbHighlight: {
    position: "absolute",
    left: 2,
    top: 1.5,
    width: 4.5,
    height: 4.5,
    borderRadius: 3,
    backgroundColor: "#FFFAE8",
  },
});
